import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import PhotoUpload from "@/components/PhotoUpload";
import { StatusChip } from "@/components/StatusChip";
import { Loader2, Save, Send, Info } from "lucide-react";
import { toast } from "sonner";

const enhVal = (f) => (f && typeof f === "object" ? f.submitted || "" : f || "");

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-border bg-card p-4 space-y-4">
    <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
    {children}
  </div>
);

export default function ShopContentProfile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("draft");
  const [meta, setMeta] = useState({});
  const [f, setF] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/shop/content-profile");
      const p = data.profile || {};
      setStatus(p.status || "draft");
      setMeta({ rejection_reason: p.rejection_reason, change_request_note: p.change_request_note });
      setF({
        listing_type: p.listing_type === "individual" ? "individual" : "business",
        name: p.name || "", address: p.address || "", phone: p.phone || "", email: p.email || "", website: p.website || "",
        about: enhVal(p.about), services: enhVal(p.services), rental_info: enhVal(p.rental_info), repair_services: enhVal(p.repair_services), amenities: enhVal(p.amenities),
        has_ebikes: !!p.has_ebikes, has_rentals: !!p.has_rentals, has_repairs: !!p.has_repairs, has_water: !!p.has_water, has_restrooms: !!p.has_restrooms, has_parking: !!p.has_parking,
        facebook: p.facebook || "", instagram: p.instagram || "", youtube: p.youtube || "", tiktok: p.tiktok || "",
        logo: p.logo || null, cover_image: p.cover_image || null, gallery: p.gallery || [],
      });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/shop/content-profile", f); setStatus(data.profile.status); toast.success("Profile saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.put("/shop/content-profile", f);
      const { data } = await api.post("/shop/content-profile/submit");
      setStatus(data.profile.status); setMeta({});
      toast.success("Submitted for review");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (loading || !f) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const Bool = ({ k, label }) => (
    <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
      <span className="text-sm">{label}</span>
      <Switch checked={f[k]} onCheckedChange={(v) => set(k, v)} data-testid={`shopcp-${k}-switch`} />
    </div>
  );

  const ind = f.listing_type === "individual";

  return (
    <div className="space-y-5 max-w-4xl" data-testid="shop-content-profile">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold">Public Listing</h2>
          <p className="text-sm text-muted-foreground">How {ind ? "you appear" : "your business appears"} to riders. Submit for review to publish changes.</p>
        </div>
        <StatusChip status={status} testId="shopcp-status" />
      </div>

      {status === "changes_requested" && meta.change_request_note && (
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 text-sm text-orange-300 flex gap-2" data-testid="shopcp-changes-note"><Info className="w-4 h-4 mt-0.5 shrink-0" />Changes requested: {meta.change_request_note}</div>
      )}
      {status === "rejected" && meta.rejection_reason && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300 flex gap-2" data-testid="shopcp-reject-note"><Info className="w-4 h-4 mt-0.5 shrink-0" />Rejected: {meta.rejection_reason}</div>
      )}

      <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="shopcp-listing-type">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">I am listing as</h3>
        <div className="grid grid-cols-2 gap-2 max-w-md">
          {[["business", "Business", "Shop, rental, café or other company"], ["individual", "Individual", "Guide, coach, instructor or other person"]].map(([k, l, d]) => (
            <button key={k} type="button" onClick={() => set("listing_type", k)} aria-pressed={f.listing_type === k}
              className={`text-left rounded-lg border px-3 py-2.5 transition-colors duration-200 ${f.listing_type === k ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              data-testid={`shopcp-listing-type-${k}`}>
              <div className="text-sm font-semibold">{l}</div>
              <div className="text-xs text-muted-foreground">{d}</div>
            </button>
          ))}
        </div>
      </div>

      <Section title={ind ? "Your Info" : "Business Info"}>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>{ind ? "Your name" : "Business name"}</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="shopcp-name-input" /></div>
          <div><Label>{ind ? "Website (optional)" : "Website"}</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid="shopcp-website-input" /></div>
          <div><Label>{ind ? "Service area" : "Address"}</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder={ind ? "e.g. Bristol & Warren, RI" : ""} data-testid="shopcp-address-input" /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="shopcp-phone-input" /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="shopcp-email-input" /></div>
        </div>
      </Section>

      <Section title={ind ? "About You & Services" : "About & Services"}>
        <div><Label>About</Label><Textarea rows={4} value={f.about} onChange={(e) => set("about", e.target.value)} placeholder={ind ? "Tell riders about yourself…" : "Tell riders about your business…"} data-testid="shopcp-about-input" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>{ind ? "What you offer" : "Services"}</Label><Textarea rows={3} value={f.services} onChange={(e) => set("services", e.target.value)} data-testid="shopcp-services-input" /></div>
          {!ind && <div><Label>Rental info</Label><Textarea rows={3} value={f.rental_info} onChange={(e) => set("rental_info", e.target.value)} data-testid="shopcp-rental_info-input" /></div>}
          {!ind && <div><Label>Repair services</Label><Textarea rows={3} value={f.repair_services} onChange={(e) => set("repair_services", e.target.value)} data-testid="shopcp-repair_services-input" /></div>}
          {!ind && <div><Label>Amenities</Label><Textarea rows={3} value={f.amenities} onChange={(e) => set("amenities", e.target.value)} data-testid="shopcp-amenities-input" /></div>}
        </div>
      </Section>

      {!ind && <Section title="Rider Amenities">
        <div className="grid sm:grid-cols-3 gap-3">
          <Bool k="has_ebikes" label="E-bikes" />
          <Bool k="has_rentals" label="Rentals" />
          <Bool k="has_repairs" label="Repairs" />
          <Bool k="has_water" label="Water" />
          <Bool k="has_restrooms" label="Restrooms" />
          <Bool k="has_parking" label="Parking" />
        </div>
      </Section>}

      <Section title="Media">
        <div className="grid sm:grid-cols-2 gap-5">
          <div><Label>{ind ? "Profile photo" : "Logo"}</Label><PhotoUpload photos={f.logo ? [f.logo] : []} onChange={(ids) => set("logo", ids[0] || null)} multiple={false} label="Logo" testId="shopcp-logo" /></div>
          <div><Label>Cover image</Label><PhotoUpload photos={f.cover_image ? [f.cover_image] : []} onChange={(ids) => set("cover_image", ids[0] || null)} multiple={false} label="Cover" testId="shopcp-cover" /></div>
        </div>
        <div><Label>Gallery</Label><PhotoUpload photos={f.gallery} onChange={(ids) => set("gallery", ids)} multiple label="Add" testId="shopcp-gallery" /></div>
      </Section>

      <Section title="Social Links">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Facebook</Label><Input value={f.facebook} onChange={(e) => set("facebook", e.target.value)} data-testid="shopcp-facebook-input" /></div>
          <div><Label>Instagram</Label><Input value={f.instagram} onChange={(e) => set("instagram", e.target.value)} data-testid="shopcp-instagram-input" /></div>
          <div><Label>YouTube</Label><Input value={f.youtube} onChange={(e) => set("youtube", e.target.value)} data-testid="shopcp-youtube-input" /></div>
          <div><Label>TikTok</Label><Input value={f.tiktok} onChange={(e) => set("tiktok", e.target.value)} data-testid="shopcp-tiktok-input" /></div>
        </div>
      </Section>

      <div className="flex justify-end gap-2 sticky bottom-0 py-3 bg-background/80 backdrop-blur">
        <Button variant="outline" onClick={save} disabled={saving || submitting} data-testid="shopcp-save">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save draft</Button>
        <Button onClick={submit} disabled={saving || submitting} data-testid="shopcp-submit">{submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Submit for review</Button>
      </div>
    </div>
  );
}
